"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function (o, m, k, k2) {
    if (k2 === undefined)
        k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
        desc = { enumerable: true, get: function () { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function (o, m, k, k2) {
    if (k2 === undefined)
        k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function (o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function (o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function (o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o)
                if (Object.prototype.hasOwnProperty.call(o, k))
                    ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule)
            return mod;
        var result = {};
        if (mod != null)
            for (var k = ownKeys(mod), i = 0; i < k.length; i++)
                if (k[i] !== "default")
                    __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.useCatalogActivityProgressContext = exports.CatalogActivityProgressProvider = void 0;
const react_1 = __importStar(require("react"));
const catalog_models_1 = require("../../client/catalog/catalog.models");
const use_catalog_activity_progress_hook_1 = require("../../client/catalog/use-catalog-activity-progress.hook");
const hooks_1 = require("../../client/hooks");
const product_tree_nodes_1 = require("../../client/product-tree-nodes");
const store_1 = require("../../../store");
const CatalogActivityProgressContext = (0, react_1.createContext)({
    isReEnrichmentInProgress: false,
    isUploadInProgress: false,
    catalogProgressState: catalog_models_1.CatalogProgressState.NotStarted,
    catalogProgress: 0,
    catalogActivityError: undefined,
    tasks: [],
    isNodeReEnriching: () => false,
    isInEnrichmentHierarchy: () => false,
    isNodeOrRelatedNodeReEnriching: () => false,
    addOptimisticReEnrich: () => { },
    clearOptimisticReEnrich: () => { },
});
const CatalogActivityProgressProvider = ({ children }) => {
    const catalogId = (0, store_1.useSelectedCatalogId)();
    // Track nodes that are optimistically marked as re-enriching: nodeId -> ancestorIds
    const [optimisticReEnrichNodes, setOptimisticReEnrichNodes] = (0, react_1.useState)(new Map());
    const { refetchProductList } = (0, hooks_1.useRefetchQueries)();
    const { tasks = [], isUploadInProgress, catalogProgressState, catalogProgress, error: catalogActivityError, } = (0, use_catalog_activity_progress_hook_1.useCatalogActivityProgress)({ selectedCatalogId: catalogId });
    // Smart auto-clear: only clear optimistic nodes that now have real tasks
    (0, react_1.useEffect)(() => {
        if (tasks.length > 0 && optimisticReEnrichNodes.size > 0) {
            setOptimisticReEnrichNodes((prev) => {
                const next = new Map(prev);
                let hasChanges = false;
                tasks.forEach((task) => {
                    if (task.targetNodeId && next.has(task.targetNodeId)) {
                        next.delete(task.targetNodeId);
                        hasChanges = true;
                        // Refetch products when real task arrives (optimistic -> real transition)
                        // This ensures products get isLocked status after backend processes the ticket
                        void refetchProductList(task.targetNodeId);
                    }
                });
                return hasChanges ? next : prev;
            });
        }
    }, [tasks, optimisticReEnrichNodes.size, refetchProductList]);
    const addOptimisticReEnrich = (0, react_1.useCallback)((nodeId, ancestorIds) => {
        setOptimisticReEnrichNodes((prev) => {
            const next = new Map(prev);
            // Exclude root node (first element) from ancestors
            const ancestorsWithoutRoot = ancestorIds.slice(1);
            next.set(nodeId, ancestorsWithoutRoot);
            return next;
        });
    }, []);
    const clearOptimisticReEnrich = (0, react_1.useCallback)((nodeId) => {
        setOptimisticReEnrichNodes((prev) => {
            const next = new Map(prev);
            next.delete(nodeId);
            return next;
        });
    }, []);
    const isNodeReEnriching = (0, react_1.useCallback)((nodeId) => (optimisticReEnrichNodes.has(nodeId) || tasks.some((task) => task.targetNodeId === nodeId)), [tasks, optimisticReEnrichNodes]);
    // check if node is in enrichment hierarchy (either as target or ancestor/descendant)
    const isInEnrichmentHierarchy = (0, react_1.useCallback)((nodePath) => {
        // Check real tasks
        for (const task of tasks) {
            if (task.targetNodeId) {
                const enrichingNodePath = (0, product_tree_nodes_1.buildTreeNodePath)([...task.ancestorNodeIds, task.targetNodeId]);
                // Check if nodePath is parent/child/ancestor/descendant of enriching node
                if ((0, product_tree_nodes_1.isDescendantPath)(nodePath, enrichingNodePath) || (0, product_tree_nodes_1.isDescendantPath)(enrichingNodePath, nodePath)) {
                    return true;
                }
            }
        }
        const childNodePath = (0, product_tree_nodes_1.buildTreeNodePath)((0, product_tree_nodes_1.getChildTreeNodeIdsFromPath)(nodePath));
        // Check optimistic state
        for (const [targetNodeId, ancestorIds] of optimisticReEnrichNodes.entries()) {
            const enrichingNodePath = (0, product_tree_nodes_1.buildTreeNodePath)([...ancestorIds, targetNodeId]);
            if ((0, product_tree_nodes_1.isDescendantPath)(childNodePath, enrichingNodePath) || (0, product_tree_nodes_1.isDescendantPath)(enrichingNodePath, childNodePath)) {
                return true;
            }
        }
        return false;
    }, [optimisticReEnrichNodes, tasks]);
    // Check if node is part of any re-enriching hierarchy (node itself, ancestors, or descendants)
    // This covers all cases where locked products could appear in the current view
    const isNodeOrRelatedNodeReEnriching = (0, react_1.useCallback)((nodeId, options) => {
        if (!(options === null || options === void 0 ? void 0 : options.onlyRealTasks)) {
            // Check optimistic state
            for (const [targetNodeId, ancestorIds] of optimisticReEnrichNodes.entries()) {
                if (targetNodeId === nodeId || ancestorIds.includes(nodeId)) {
                    return true;
                }
            }
        }
        // Check real tasks
        return tasks.some((task) => task.targetNodeId === nodeId
            || task.ancestorNodeIds.includes(nodeId));
    }, [tasks, optimisticReEnrichNodes]);
    const value = (0, react_1.useMemo)(() => ({
        isReEnrichmentInProgress: tasks.length > 0 || optimisticReEnrichNodes.size > 0,
        isUploadInProgress,
        catalogProgressState,
        catalogProgress,
        catalogActivityError,
        tasks,
        isNodeReEnriching,
        isInEnrichmentHierarchy,
        isNodeOrRelatedNodeReEnriching,
        addOptimisticReEnrich,
        clearOptimisticReEnrich,
    }), [
        tasks,
        isUploadInProgress,
        catalogProgressState,
        catalogProgress,
        catalogActivityError,
        isNodeReEnriching,
        isInEnrichmentHierarchy,
        isNodeOrRelatedNodeReEnriching,
        addOptimisticReEnrich,
        clearOptimisticReEnrich,
        optimisticReEnrichNodes.size,
    ]);
    return (react_1.default.createElement(CatalogActivityProgressContext.Provider, { value: value }, children));
};
exports.CatalogActivityProgressProvider = CatalogActivityProgressProvider;
const useCatalogActivityProgressContext = () => {
    const context = react_1.default.useContext(CatalogActivityProgressContext);
    return context;
};
exports.useCatalogActivityProgressContext = useCatalogActivityProgressContext;
